import $ from 'jquery';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {asCoreObjectId} from 'app-core/framework/CoreObjectIntrospectionUtils';
import * as StarDAO from 'app-core/platform/starrable/StarDAO';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isBiologicsOwnedItem, StandardFeatureItemState} from 'auth/capabilities/getCapabilityFeatures';
import {warnAndRedirectAfterInaccessibleItemOpen} from 'auth/capabilities/InaccessibleFeaturesActions';
import {getFeatureStatus} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import {AlignmentPolymerType} from 'constants/AlignmentConstants';
import {
  ActionOrigin,
  MOLBIO_ANALYTICS_ACTIONS,
  REGISTRY_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import type {FileBrowserEventPayload} from 'constants/events/FileBrowserEventPayloads';
import type {WorkspaceItemDispatchItem} from 'constants/FileBrowserConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {NavStoreSource, ProjectSummary} from 'constants/SourceConstants';
import FileState from 'filestate/FileState';
import {BioentitySummary, FileSummary} from 'folder-items/FileConstants';
import AppDispatcher from 'lib/Dispatcher';
import navigateToURL from 'lib/navigateToURL';
import {openChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {modelFetched} from 'models/ManagedModelsReduxActions';
import notifications from 'notifications';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import {navigateToStudyOverviewTab} from 'studies/StudyNavigationUtils';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import APIUtil from 'util/APIUtil';

import * as FileBrowserDAO from './FileBrowserDAO';
import FolderDAO from './FolderDAO';
import SourceDAO from './SourceDAO';

export type FileBrowserAction = FileUpdateTagSchemaAction;

type FileUpdateTagSchemaAction = {
  type: typeof EventConstants.FILE_TAG_SCHEMA_UPDATED;
} & FileBrowserEventPayload['FILE_TAG_SCHEMA_UPDATED'];

export interface FileBrowserFolderItem {
  id: string;
  name: string;
  isBiologicsOwned?: boolean | null;
  tagSchema?: TagSchema | null;
}

export interface FileOpenParams {
  alignment?: string;
  batch?: string;
  tab?: string;
  open?: boolean;
  prefetch?: boolean;
  folderItem?: FileBrowserFolderItem;
  itemState?: StandardFeatureItemState;
  toolbarTrigger?: string;
  fromRegistry?: boolean;
  onOpen?: (fileState: FileState | undefined) => void;
  versionId?: number;
  versionApiId?: string;
  analyticsMetadata?: {[key: string]: string | boolean | null};
  openManageAccess?: boolean;
  runId?: string;
}

function resolveOpenFileItemState(
  itemState: StandardFeatureItemState | undefined,
  folderItem: FileBrowserFolderItem | undefined
): StandardFeatureItemState {
  if (itemState != null) {
    return itemState;
  }
  if (folderItem != null) {
    return {
      isRegistered: false,
      isBiologicsOwned: isBiologicsOwnedItem(folderItem),
    };
  }
  return {isRegistered: false, isBiologicsOwned: false};
}

function resolveWorkspaceItemFields(item?: FileBrowserFolderItem): WorkspaceItemDispatchItem {
  if (item == null) {
    return {name: 'Loading...', tagSchema: null};
  }
  return {
    name: item.name ?? 'Loading...',
    tagSchema: item.tagSchema ?? null,
  };
}

// Sends rename request to the server and returns a Promise that resolves when the
// request is completed.
async function renameFile(
  id: string,
  {
    oldName,
    newName,
    parentFolderId,
    notifyOnSuccess,
  }: {oldName?: string; newName: string; parentFolderId?: string; notifyOnSuccess?: boolean},
  dataModsHeader?: DataModificationsHeader
): Promise<void> {
  if (notifyOnSuccess == null) {
    notifyOnSuccess = true;
  }
  try {
    const response = await FileBrowserDAO.updateApiIdentified(
      id,
      {name: newName},
      {parentFolderId},
      dataModsHeader
    );

    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_BROWSER_RENAME_FILE,
      id,
      name: response.name,
      displayLabel: response.displayLabel,
      bioentityAliasInfos: response.bioentityAliasInfos,
    });

    if (notifyOnSuccess) {
      notifications.success(
        <>
          Renamed <strong>{oldName}</strong> to <strong>{newName}</strong>
        </>
      );
    }
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (err) {
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_BROWSER_RENAME_FILE_FAILED,
    });
    notifications.notifyError(err);
  }
}

async function updateAuthors(
  fileId: string,
  authorIds: Array<string>,
  dataModsHeader?: DataModificationsHeader
): Promise<void> {
  try {
    const {authors, filePermissions} = await FileBrowserDAO.updateAuthors(fileId, authorIds, dataModsHeader);
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_AUTHORS_UPDATED,
      fileId,
      authors,
      newPermissions: filePermissions,
    });
    getStore().dispatch(modelFetched(ManagedModelType.AUTHORS_BY_ITEM_ID, {[fileId]: authors}));
    notifications.success('Successfully updated authors.');
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (err) {
    notifications.notifyError(err);
  }
}

const FileBrowserActions = {
  openMultipleItems: (items: Array<FileBrowserFolderItem>): void => {
    // Open the items in reverse order so they'll show up in order in the UI. The first item in the
    // list should end up as the active tab, so use openItem for that one.
    for (let i = items.length - 1; i >= 0; i--) {
      const item = items[i];
      if (i > 0) {
        FileBrowserActions.addToWorkspace(item.id, item);
      } else {
        FileBrowserActions.openItem(item.id);
      }
    }
  },

  // Opening to a certain tab calls through to openFile, so we create a separate function to avoid
  // an infinite loop.
  openItem: (itemId: string, params: FileOpenParams = {}): void => {
    const {alignment: alignmentId, runId} = params;

    if (alignmentId) {
      const fileType = APIUtil.apiIdToMaybeType(itemId);
      if ([ModelTypeConstants.SEQUENCE, ModelTypeConstants.PROTEIN].includes(fileType!)) {
        const status = getFeatureStatus({feature: MolbioFeature.MOLBIO_OPEN});
        if (!status.enabled) {
          warnAndRedirectAfterInaccessibleItemOpen(itemId, status.userMessage);
          return;
        }
      }

      if (fileType === ModelTypeConstants.SEQUENCE) {
        analytics.deprecatedTrackEvent(MOLBIO_ANALYTICS_ACTIONS.LOADED_ALIGNMENT_URL, {
          residueType: AlignmentPolymerType.DNA,
        });
        const DNAAlignmentActions = require('analyses/alignment/AlignmentActions');
        DNAAlignmentActions.open({sequenceId: itemId, alignmentId});
      } else if (fileType === ModelTypeConstants.PROTEIN) {
        analytics.deprecatedTrackEvent(MOLBIO_ANALYTICS_ACTIONS.LOADED_ALIGNMENT_URL, {
          residueType: AlignmentPolymerType.AA,
        });
        const ProteinAlignmentActions =
          require('molbio/aa-sequences/alignments/AaSequenceAlignmentActions').default;
        ProteinAlignmentActions.open({proteinId: itemId, alignmentId});
      }
      return;
    }
    if (runId) {
      const fileType = APIUtil.apiIdToMaybeType(itemId);
      if (fileType === ModelTypeConstants.STUDY) {
        navigateToStudyOverviewTab(itemId, runId);
        return;
      }
    }

    FileBrowserActions.openFile(itemId, params);
  },

  // TODO(T3619): Generalize naming since this can open non-files as well
  openFile: (
    fileId: string,
    {
      open,
      prefetch,
      folderItem,
      itemState,
      toolbarTrigger,
      fromRegistry,
      onOpen,
      versionId,
      versionApiId,
      analyticsMetadata,
      openManageAccess,
    }: FileOpenParams = {}
  ): void => {
    const FileStates = require('FileStates').default;

    const status = getFeatureStatus({
      feature: StandardFeature.OPEN,
      item: {id: fileId},
      itemState: resolveOpenFileItemState(itemState, folderItem),
    });
    if (!status.enabled) {
      warnAndRedirectAfterInaccessibleItemOpen(fileId, status.userMessage);
      return;
    }

    const fileType = APIUtil.apiIdToMaybeType(fileId);
    if (fileType === ModelTypeConstants.FOLDER) {
      FileStates.get().openFolder(fileId);
      return;
    } else if (fileType === ModelTypeConstants.CHAT_SESSION) {
      // This just opens the chat in the sidepanel. We rely on the sidepanel to handle rewriting the URL.
      openChat(fileId);
      return;
    }

    if (prefetch == null) {
      prefetch = true;
    }
    if (open == null) {
      open = true;
    }

    if (open) {
      $('#fileLoading').removeClass('hidden');
    }
    if (prefetch || open) {
      FileStates.get()
        .openFolderItem(fileId, {
          open,
          versionId,
          versionApiId,
          item: folderItem,
          source: 'eln-file-browser',
          analyticsMetadata,
          openManageAccess,
        })
        .done((fileState?: FileState) => {
          if (fromRegistry) {
            analytics.deprecatedTrackEvent(REGISTRY_ANALYTICS_ACTIONS.OPENED_REGISTERED_FILE);
          }

          if (toolbarTrigger != null) {
            fileState!.get('toolbar').trigger(toolbarTrigger);
          }
          if (onOpen != null) {
            onOpen(fileState);
          }
        })
        .always(() => {
          if (open) {
            $('#fileLoading').addClass('hidden');
          }
        });
    } else {
      // NOTE: This is temporary. Since many places in the codebase still call
      // fileStates.openFolderItem directly, openFolderItem calls addToWorkspace already.
      // After we refactor calls to openFolderItem to go through openFile, we can remove this
      // else check only (and always) addToWorkspace here.
      // See T1610.
      if (fromRegistry) {
        analytics.deprecatedTrackEvent(REGISTRY_ANALYTICS_ACTIONS.OPENED_REGISTERED_FILE);
      }
      FileBrowserActions.addToWorkspace(fileId, folderItem);
    }
  },

  addToWorkspace: (fileId: string, item?: FileBrowserFolderItem): void => {
    // TODO(T4223): Remove this copy in favor of passing in name with undefined value and delegating
    // responsibility downstream
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_BROWSER_ADD_TO_WORKSPACE,
      item: resolveWorkspaceItemFields(item),
      fileId,
    });
  },

  renameFileWithDataMods: async (
    id: string,
    isRegistered: boolean,
    {
      oldName,
      newName,
      parentFolderId,
      notifyOnSuccess,
    }: {oldName?: string; newName: string; parentFolderId?: string; notifyOnSuccess?: boolean}
  ): Promise<void> => {
    await callAsyncWithDataModifications({
      changeSummary: isRegistered
        ? getInventoryRegistryChangeSummary({apiIds: [id], action: InventoryRegistryDataModAction.MODIFY})
        : 'Updating name',
      onPerformAction: async (dataModsHeader?) => {
        await renameFile(id, {oldName, newName, parentFolderId, notifyOnSuccess}, dataModsHeader);
      },
      apiIds: [id],
      analyticsParams: {
        module: 'REGISTRY',
        actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
        modifiedAction: InventoryRegistryDataModAction.MODIFY,
        numModifiedTotalItems: 1,
        entryId: null,
      },
      useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY,
    });
  },

  deleteSource: (id: string): JQueryPromise<void> => {
    LoadingScreen.show();
    return SourceDAO.delete_(id)
      .always(() => {
        LoadingScreen.hide();
      })
      .done(() => {
        AppDispatcher.dispatch({
          actionType: EventConstants.FILE_BROWSER_SOURCE_DELETED,
          id,
        });
      })
      .fail(notifications.notifyError);
  },

  openDefaultHomeState: (): void => {
    navigateToURL('/editor');
  },

  removeFromWorkspace: (fileId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_BROWSER_REMOVE_FROM_WORKSPACE,
      fileId,
    });
  },

  bulkRemoveFromWorkspace: (fileIds: Array<string>): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_BROWSER_BULK_REMOVE_FROM_WORKSPACE,
      fileIds,
    });
  },

  moveWorkspaceTab: (workspaceItemId: string, toIndex: number): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_BROWSER_MOVE_WORKSPACE_TAB,
      workspaceItemId,
      toIndex,
    });
  },

  clearWorkspace: (itemIdsToKeep: Array<string>): void => {
    if (itemIdsToKeep == null) {
      itemIdsToKeep = [];
    }
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_BROWSER_CLEAR_WORKSPACE,
      itemIdsToKeep,
    });
  },

  updateWorkspaceWidth: (workspaceWidth: number): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.WORKSPACE_WIDTH_UPDATED,
      workspaceWidth,
    });
  },

  fetchSources: (): JQueryPromise<{data: Array<NavStoreSource>}> =>
    FileBrowserDAO.fetchSources().done((response: {data: Array<NavStoreSource>}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.FILE_BROWSER_SOURCES_FETCHED,
        sources: response.data,
      });
    }),

  fetchRecents: (): JQueryPromise<{data: Array<FileSummary>}> =>
    FileBrowserDAO.fetchRecents().done((response: {data: Array<FileSummary>}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.FILE_BROWSER_RECENTS_FETCHED,
        recents: response.data,
      });
    }),

  bulkFetchFileSummaries: (
    fileIds: Array<string>,
    {shouldErrorIfInaccessible}: {shouldErrorIfInaccessible: boolean} = {shouldErrorIfInaccessible: false}
  ): void => {
    FileBrowserDAO.bulkFetchFileSummaries(fileIds, shouldErrorIfInaccessible).done(
      ({fileSummariesById}: {fileSummariesById: {[id: string]: BioentitySummary}}) => {
        // [BNCH-11628] Keep our backbone store in sync with any updated tag schema or tags
        getStore().dispatch(ModelActions.filesFetchCompleted(fileSummariesById));
        for (const fileId of Object.keys(fileSummariesById)) {
          AppDispatcher.dispatch({
            actionType: EventConstants.FILE_TAG_SCHEMA_UPDATED,
            fileId,
            tagSchema: fileSummariesById[fileId].tagSchema,
            tags: fileSummariesById[fileId].tags,
          });
          getStore().dispatch({
            type: EventConstants.FILE_TAG_SCHEMA_UPDATED,
            fileId,
            tagSchema: fileSummariesById[fileId].tagSchema,
            tags: fileSummariesById[fileId].tags,
          });
        }
      }
    );
  },

  moveItemsWithDataMods: async (
    fileIds: Array<string>,
    targetFolderId: string,
    {parentFolderIds, hasRegisteredItems}: {parentFolderIds: Array<string>; hasRegisteredItems?: boolean},
    shortcutPredicate?: (fileId: string) => boolean
  ): Promise<void> => {
    const entityIds = fileIds.filter((id) => APIUtil.isBiologicalEntity(id));
    const storableIds = fileIds.filter((id) => APIUtil.isStorable(id));
    const hasStorablesOrRegisteredItems = hasRegisteredItems || storableIds.length > 0;
    const modifiedIds = [...entityIds, ...storableIds];

    await callAsyncWithDataModifications({
      changeSummary: hasStorablesOrRegisteredItems
        ? getInventoryRegistryChangeSummary({
            apiIds: modifiedIds,
            action: InventoryRegistryDataModAction.MODIFY,
          })
        : `Moving ${fileIds.length} ${BenchlingUtil.pluralize(fileIds.length, 'item')}`,
      onPerformAction: async (dataModsHeader?: DataModificationsHeader) => {
        await FileBrowserActions.moveItems(
          fileIds,
          targetFolderId,
          {parentFolderIds},
          shortcutPredicate,
          dataModsHeader
        );
      },
      analyticsParams: {
        module: 'NAVIGATION',
        actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
        modifiedAction: InventoryRegistryDataModAction.MODIFY,
        numModifiedTotalItems: modifiedIds.length,
        entryId: null,
      },
      apiIds: fileIds,
      useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_PROJECTS_FOLDERS,
      useFolderMoveDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_PROJECTS_FOLDERS
        ? {apiIds: fileIds, destinationFolderApiId: targetFolderId}
        : undefined,
    });
  },

  moveItems: async (
    fileIds: Array<string>,
    targetFolderId: string,
    {parentFolderIds}: {parentFolderIds: Array<string>},
    shortcutPredicate?: (fileId: string) => boolean,
    dataModsHeader?: DataModificationsHeader
  ): Promise<void> => {
    if (shortcutPredicate == null) {
      shortcutPredicate = (fileId: string) => false;
    }
    const [shortcutFileIds, nonShortcutFileIds] = Array.from(_.partition(fileIds, shortcutPredicate));

    const movePromises = [];

    if (nonShortcutFileIds.length) {
      const nonShortcutMovePromise = (async () => {
        try {
          await FileBrowserDAO.moveFiles(
            nonShortcutFileIds,
            targetFolderId,
            {
              parentFolderIds,
            },
            dataModsHeader
          );
          const folderResp = await FolderDAO.fetch(targetFolderId, {summary: '1'});
          AppDispatcher.dispatch({
            actionType: EventConstants.FILE_BROWSER_MOVE_FILES_COMPLETED,
            ids: nonShortcutFileIds,
            folder: folderResp,
          });
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (err) {
          notifications.notifyError(err);
        }
      })();
      movePromises.push(nonShortcutMovePromise);
    }

    if (shortcutFileIds.length) {
      const shortcutMovePromise = (async () => {
        try {
          await FileBrowserDAO.moveShortcuts(
            shortcutFileIds,
            targetFolderId,
            {
              parentFolderIds,
            },
            dataModsHeader
          );
          if (targetFolderId) {
            const folderResp = await FolderDAO.fetch(targetFolderId, {summary: '1'});
            AppDispatcher.dispatch({
              actionType: EventConstants.FILE_BROWSER_MOVE_SHORTCUTS_COMPLETED,
              ids: shortcutFileIds,
              folder: folderResp,
            });
          } else {
            AppDispatcher.dispatch({
              actionType: EventConstants.FILE_BROWSER_MOVE_SHORTCUTS_COMPLETED,
              ids: shortcutFileIds,
              folder: null,
            });
          }
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (err) {
          notifications.notifyError(err);
        }
      })();
      movePromises.push(shortcutMovePromise);
    }

    await Promise.all(movePromises);

    notifications.success(
      `Successfully moved ${fileIds.length} ${BenchlingUtil.pluralize(fileIds.length, 'item')}.`
    );
  },

  copyItems: (fileIds: Array<string>, targetFolderId: string): JQueryPromise<{fileCopyIds: Array<string>}> =>
    FileBrowserDAO.copyFiles(fileIds, targetFolderId)
      .done(() => {
        notifications.success(`Successfully copied ${fileIds.length} \
${fileIds.length === 1 ? 'item' : 'items'}.`);
      })
      .fail(notifications.notifyError),

  copyAndOpenItem: (fileId: string, targetFolderId: string): void => {
    FileBrowserActions.copyItems([fileId], targetFolderId).done(
      ({fileCopyIds}: {fileCopyIds: Array<string>}) => {
        FileBrowserActions.openFile(fileCopyIds[0]);
      }
    );
  },

  starSource: async (project: ProjectSummary): Promise<void> => {
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_BROWSER_SOURCE_UPDATED,
      sourceId: project.id,
      sourceUpdates: {starred: true, hidden: false}, // This matches the behavior of star_source_for_user on the server
    });
    await StarDAO.starItems([{id: asCoreObjectId(project.rootFolder.api_identifier)}]);
  },

  unstarSource: async (project: ProjectSummary): Promise<void> => {
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_BROWSER_SOURCE_UPDATED,
      sourceId: project.id,
      sourceUpdates: {starred: false},
    });
    await StarDAO.unstarItems([{id: asCoreObjectId(project.rootFolder.api_identifier)}]);
  },

  toggleProjectStar: async (project: ProjectSummary): Promise<void> => {
    if (project.starred) {
      await FileBrowserActions.unstarSource(project);
      analytics.track('DATA_MANAGEMENT', 'unstarred_project', {projectId: project.id});
    } else {
      await FileBrowserActions.starSource(project);
      analytics.track('DATA_MANAGEMENT', 'starred_project', {projectId: project.id});
    }
  },

  hideSources: (sourceIds: Array<string>): void => {
    SourceDAO.hide(sourceIds);
  },

  unhideSource: (sourceId: string): void => {
    SourceDAO.unhide([sourceId]);
  },

  exportTagsToCSV: (fileIds: Array<string>): void => {
    const notificationId = _.uniqueId('export-file-tags-to-csv-');
    notifications.info('Exporting...', {id: notificationId, duration: 0});
    FileBrowserDAO.exportTagsToCSV(fileIds)
      .done((successResponse: Response) => {
        BenchlingUtil.redirectForDownload(successResponse.url);
        notifications.success('Export complete. Downloading...', {id: notificationId});
      })
      .fail((errorResponse: Response) => {
        notifications.notifyError(errorResponse, {id: notificationId});
      });
  },

  updateAuthorsWithDataMods: async (
    fileId: string,
    authorIds: Array<string>,
    isRegistered: boolean
  ): Promise<void> => {
    await callAsyncWithDataModifications({
      changeSummary: isRegistered
        ? getInventoryRegistryChangeSummary({
            apiIds: [fileId],
            action: InventoryRegistryDataModAction.MODIFY,
          })
        : '',
      onPerformAction: (dataModsHeader?) => {
        updateAuthors(fileId, authorIds, dataModsHeader);
      },
      apiIds: [fileId],
      analyticsParams: {
        module: 'REGISTRY',
        actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
        modifiedAction: InventoryRegistryDataModAction.MODIFY,
        numModifiedTotalItems: 1,
        entryId: null,
      },
      useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY && isRegistered,
    });
  },
};

export default FileBrowserActions;
